import React, { useEffect, useState } from "react";
import { get, omit } from "lodash";
import moment from "moment";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useStores } from "_common/hooks";
import { useForm } from "react-hook-form";

import { observer } from "mobx-react";
import { NotificationManager } from "react-notifications";
import { ProfileApi } from "states/api";
import UploadFile from "_common/component/UploadFile";
import i18next from "i18next";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";

const schema = yup
  .object({
    full_name: yup.string().required(i18next.t("verify_require")),
  })
  .required();

const KYCForm = observer(() => {
  const navigate = useNavigate();
  const {
    authStore: { user },
  } = useStores();
  const queryClient = useQueryClient();
  const [front_images, setFrontImage] = useState();
  const [back_images, setBackImage] = useState();

  const { mutate, isLoading } = useMutation(
    (variables) => ProfileApi.update({ params: variables }),
    {
      onSuccess: (res) => {
        navigate("/member");
        queryClient.invalidateQueries(["get_profile"]);
        NotificationManager.success(
          i18next.t(res?.msg) || i18next.t("action_success_msg")
        );
      },
      onError: (error) => {
        const errorMessage =
          i18next.t(error?.message) ?? i18next.t("action_failed_msg");
        NotificationManager.error(errorMessage);
      },
    }
  );

  const {
    register,
    trigger,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSave = (values) => {
    if (isLoading) return;
    trigger();
    const formValue = getValues();
    const isValid = schema.isValidSync(formValue);
    if (isValid) {
      mutate({
        ...formValue,
        front_image: front_images,
        back_image: back_images,
      });
    }
  };

  const onReceiveFrontImages = (images) => {
    setFrontImage(images[0]?.url);
  };

  const onReceiveBackImages = (images) => {
    setBackImage(images[0]?.url);
  };

  useEffect(() => {
    if (user?.back_image) {
      setBackImage(user?.back_image_url);
    }
    if (user?.front_image) {
      setFrontImage(user?.front_image_url);
    }
  }, [user]);

  return (
    <form onSubmit={handleSubmit(onSave)}>
      <div className="imgbox !bg-[transparent]">
        <div className="imgbox_4 !border-[0] !m-[0]">
          <div className="imgbox_5">
            <span className="fzmmm fe6im f12">Tên tài khoản</span>
          </div>
          <div className="zz_inputbox">
            <input
              type="text"
              placeholder="Vui lòng nhập tên tài khoản"
              className="zz_input "
              disabled={user?.is_verify === 1}
              {...register("full_name")}
            />
          </div>
          <div className="imgbox_5">
            <span className="fzmmm fe6im f12">Xác thực</span>
          </div>
          <div className="zz_inputbox flex items-center">
            <select
              placeholder={i18next.t("type_kyc")}
              className="inputbox-select"
              disabled={user?.is_verify === 1}
            >
              <option value="cmnd">{i18next.t("id_card")}</option>
              <option value="passport">{i18next.t("passport")}</option>
              <option value="driver_license">{i18next.t("license")}</option>
            </select>
            <FontAwesomeIcon icon={faChevronDown} color="white" />
          </div>
          <div className="imgbox_5">
            <span className="fzmmm fe6im f12">Số CMND/CCCD</span>
          </div>
          <div className="zz_inputbox">
            <input
              type="text"
              placeholder="Vui lòng nhập số CMND/CCCD"
              className="zz_input "
              disabled={user?.is_verify === 1}
              {...register("identify_number")}
            />
          </div>
          <div className="imgbox_5">
            <span className="fzmmm fe6im f12">Tải lên tài liệu</span>
          </div>
          <div className="imgbox_6 rounded-[10px]">
            <UploadFile
              onReceiveImages={onReceiveFrontImages}
              image={front_images}
              folder="kyc"
              disabled={user?.is_verify === 1}
            />
          </div>
          <div className="imgbox_5">
            <span className="fzmmm fe6im f12">Tải lên chứng chỉ</span>
          </div>
          <div className="imgbox_6 rounded-[10px]">
            <UploadFile
              onReceiveImages={onReceiveBackImages}
              image={back_images}
              folder="kyc"
              disabled={user?.is_verify === 1}
            />
          </div>
          <div className="imgbox_8 mt-[20px]">
            <div className="imgbox_10 !bg-[#3db485] w-full" id="sumbtn">
              <button type="submit" className="w-full">
                {user?.is_verify === 1
                  ? "Đánh giá chứng nhận thành công"
                  : i18next.t("send")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
});

export default KYCForm;
